import { useEffect, useMemo } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Activity,
  Clock,
  HardDrive,
  AlertCircle,
  Sparkles,
  Loader2,
  ArrowUpRight,
  Globe,
  Smartphone,
  Monitor,
  Filter,
  AlertOctagon,
  Lock,
  EyeOff,
  Server,
  Zap
} from 'lucide-react';
import { useHARStore } from '@/stores/har-store';
import { formatBytes, formatTime, getFileName } from '@har-insight/parser';
import { cn } from '@/lib/utils';
import type { HAREntry } from '@har-insight/parser';

interface AIBasicInsight {
  overallStatus: 'healthy' | 'degraded' | 'critical';
  summary: string;
  failedRequests?: Array<{
    url: string;
    status: number;
    time: number;
    explanation: string;
  }>;
  slowRequests?: Array<{
    url: string;
    time: number;
    threshold: string;
    explanation: string;
  }>;
  recommendations?: string[];
  errorPatterns: Array<{
    pattern: string;
    count: number;
    statusCode: number;
    examples: string[];
    likelyCause: string;
  }>;
}

interface UserContext {
  browser: string;
  os: string;
  device: string;
  platform: string;
  userAgent: string;
  ip?: string;
}

interface FilterPreset {
  id: string;
  label: string;
  icon: React.ElementType;
  filter: {
    statusCodes?: string[];
    methods?: string[];
    timeThreshold?: number;
  };
  color: 'red' | 'amber' | 'blue' | 'gray';
}

const FILTER_PRESETS: FilterPreset[] = [
  { id: 'errors', label: 'All Errors (4xx/5xx)', icon: AlertOctagon, filter: { statusCodes: ['4xx', '5xx'] }, color: 'red' },
  { id: 'auth', label: 'Auth Issues (401/403)', icon: Lock, filter: { statusCodes: ['401', '403'] }, color: 'amber' },
  { id: 'notfound', label: 'Not Found (404)', icon: EyeOff, filter: { statusCodes: ['404'] }, color: 'blue' },
  { id: 'server', label: 'Server Errors (5xx)', icon: Server, filter: { statusCodes: ['5xx'] }, color: 'red' },
  { id: 'slow', label: 'Slow Requests (>1s)', icon: Clock, filter: { timeThreshold: 1000 }, color: 'amber' },
  { id: 'veryslow', label: 'Very Slow (>3s)', icon: Zap, filter: { timeThreshold: 3000 }, color: 'red' },
];

function parseUserAgent(userAgent: string): UserContext {
  const ua = userAgent.toLowerCase();

  let browser = 'Unknown';
  if (ua.includes('chrome')) browser = 'Chrome';
  else if (ua.includes('firefox')) browser = 'Firefox';
  else if (ua.includes('safari') && !ua.includes('chrome')) browser = 'Safari';
  else if (ua.includes('edge')) browser = 'Edge';

  let os = 'Unknown';
  if (ua.includes('windows')) os = 'Windows';
  else if (ua.includes('mac')) os = 'macOS';
  else if (ua.includes('iphone') || ua.includes('ipad')) os = 'iOS';
  else if (ua.includes('android')) os = 'Android';
  else if (ua.includes('linux')) os = 'Linux';

  let device = 'Desktop';
  if (ua.includes('mobile') || ua.includes('android') || ua.includes('iphone')) device = 'Mobile';
  else if (ua.includes('tablet') || ua.includes('ipad')) device = 'Tablet';

  return {
    browser,
    os,
    device,
    platform: navigator.platform || 'Unknown',
    userAgent: userAgent.substring(0, 100),
  };
}

function StatPill({ icon: Icon, label, value, status, onClick }: {
  icon: React.ElementType;
  label: string;
  value: string;
  status?: 'good' | 'warning' | 'bad';
  onClick?: () => void;
}) {
  return (
    <button
      onClick={onClick}
      className={cn(
        'flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 transition-all',
        onClick && 'hover:border-violet-300 dark:hover:border-violet-500 hover:shadow-sm',
        status === 'bad' && 'border-red-300 dark:border-red-500/30 bg-red-50 dark:bg-red-500/5',
        status === 'warning' && 'border-amber-300 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/5'
      )}
    >
      <Icon className={cn(
        'w-4 h-4',
        status === 'good' && 'text-emerald-500',
        status === 'warning' && 'text-amber-500',
        status === 'bad' && 'text-red-500'
      )} />
      <div>
        <p className="text-xs text-slate-500 dark:text-slate-400">{label}</p>
        <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">{value}</p>
      </div>
    </button>
  );
}

function UserContextCard({ context }: { context: UserContext }) {
  return (
    <div className="p-4 bg-slate-50 dark:bg-slate-800/50 rounded-xl border border-slate-200 dark:border-slate-700">
      <div className="flex items-center gap-2 mb-3">
        <Globe className="w-4 h-4 text-slate-500" />
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          User Context
        </span>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="flex items-center gap-2">
          <Monitor className="w-4 h-4 text-slate-400" />
          <div>
            <p className="text-[10px] text-slate-500">Browser</p>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{context.browser}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Smartphone className="w-4 h-4 text-slate-400" />
          <div>
            <p className="text-[10px] text-slate-500">OS</p>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{context.os}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {context.device === 'Mobile' ? (
            <Smartphone className="w-4 h-4 text-slate-400" />
          ) : (
            <Monitor className="w-4 h-4 text-slate-400" />
          )}
          <div>
            <p className="text-[10px] text-slate-500">Device</p>
            <p className="text-sm font-medium text-slate-700 dark:text-slate-300">{context.device}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

function ErrorPatternGroup({ pattern }: {
  pattern: AIBasicInsight['errorPatterns'][0]
}) {
  const { filteredEntries, selectEntry, setSidebarOpen } = useHARStore();

  const matchExample = (url: string): HAREntry | null => {
    return filteredEntries.find(e => e.request.url.includes(url)) || null;
  };

  const handleClick = (url: string) => {
    const entry = matchExample(url);
    if (entry) {
      selectEntry(entry);
      setSidebarOpen(true);
    }
  };

  const statusColor = pattern.statusCode >= 500 ? 'red' : pattern.statusCode >= 400 ? 'amber' : 'blue';

  return (
    <button
      onClick={() => pattern.examples[0] && handleClick(pattern.examples[0])}
      className="w-full p-4 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900/50 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all text-left"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <span className={cn(
              'text-xs font-bold px-2 py-0.5 rounded',
              statusColor === 'red' && 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-400',
              statusColor === 'amber' && 'bg-amber-100 dark:bg-amber-500/20 text-amber-700 dark:text-amber-400',
              statusColor === 'blue' && 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-400'
            )}>
              {pattern.statusCode} × {pattern.count}
            </span>
            <span className="text-sm font-medium text-slate-900 dark:text-slate-100">
              {pattern.pattern}
            </span>
          </div>
          <p className="text-xs text-slate-600 dark:text-slate-400 mb-2">
            {pattern.likelyCause}
          </p>
          <div className="flex flex-wrap gap-1">
            {pattern.examples.slice(0, 3).map((url: string, i: number) => (
              <span
                key={i}
                className="text-[10px] px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded text-slate-500 dark:text-slate-400 truncate max-w-[200px]"
              >
                {getFileName(url)}
              </span>
            ))}
            {pattern.examples.length > 3 && (
              <span className="text-[10px] text-slate-400">+{pattern.examples.length - 3} more</span>
            )}
          </div>
        </div>
        <ArrowUpRight className="w-4 h-4 text-slate-400 flex-shrink-0 mt-1" />
      </div>
    </button>
  );
}

function FilterPresets() {
  const { har, filters, setFilters, clearFilters } = useHARStore();
  const allEntries = har?.log.entries ?? [];

  const hasNonPresetFilters = useMemo(() => {
    return Boolean(
      filters.search ||
      filters.isRegex ||
      filters.methods.length > 0 ||
      filters.contentTypes.length > 0 ||
      filters.domains.length > 0 ||
      filters.minSize !== undefined ||
      filters.maxSize !== undefined ||
      filters.maxTime !== undefined
    );
  }, [filters]);

  const activeFilter = useMemo(() => {
    if (hasNonPresetFilters) return null;

    for (const preset of FILTER_PRESETS) {
      if (preset.filter.statusCodes) {
        const current = [...filters.statusCodes].sort().join(',');
        const expected = [...preset.filter.statusCodes].sort().join(',');
        if (current === expected && filters.minTime === undefined) {
          return preset.id;
        }
      } else if (preset.filter.timeThreshold) {
        if (filters.minTime === preset.filter.timeThreshold && filters.statusCodes.length === 0) {
          return preset.id;
        }
      }
    }

    return null;
  }, [filters, hasNonPresetFilters]);

  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    FILTER_PRESETS.forEach(preset => {
      if (preset.filter.statusCodes) {
        result[preset.id] = allEntries.filter(e => {
          const status = e.response.status;
          const statusGroup = `${Math.floor(status / 100)}xx`;
          const statusStr = status.toString();
          return preset.filter.statusCodes!.includes(statusGroup) || preset.filter.statusCodes!.includes(statusStr);
        }).length;
      } else if (preset.filter.timeThreshold) {
        result[preset.id] = allEntries.filter(e =>
          e.time > preset.filter.timeThreshold!
        ).length;
      }
    });
    return result;
  }, [allEntries]);

  const handleFilter = (preset: FilterPreset) => {
    if (activeFilter === preset.id) {
      clearFilters();
    } else {
      clearFilters();
      if (preset.filter.statusCodes) {
        setFilters({ statusCodes: preset.filter.statusCodes });
      } else if (preset.filter.timeThreshold) {
        setFilters({ minTime: preset.filter.timeThreshold });
      }
    }
  };

  return (
    <div className="card p-4">
      <div className="flex items-center gap-2 mb-3">
        <Filter className="w-4 h-4 text-slate-500" />
        <span className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
          Quick Filters
        </span>
        {activeFilter && (
          <button
            onClick={() => {
              clearFilters();
            }}
            className="ml-auto text-xs text-violet-600 dark:text-violet-400 hover:underline"
          >
            Clear
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-2">
        {FILTER_PRESETS.map(preset => {
          const Icon = preset.icon;
          const count = counts[preset.id];
          const isActive = activeFilter === preset.id;

          return (
            <button
              key={preset.id}
              onClick={() => handleFilter(preset)}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-medium transition-all',
                isActive
                  ? 'bg-violet-100 dark:bg-violet-500/20 text-violet-700 dark:text-violet-300 border border-violet-300 dark:border-violet-500/30'
                  : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-700 border border-transparent'
              )}
            >
              <Icon className={cn(
                'w-3.5 h-3.5',
                isActive ? 'text-violet-500' : 'text-slate-400'
              )} />
              <span>{preset.label}</span>
              {count > 0 && (
                <span className={cn(
                  'px-1.5 py-0.5 rounded-full text-[10px]',
                  isActive
                    ? 'bg-violet-200 dark:bg-violet-500/30 text-violet-700 dark:text-violet-300'
                    : 'bg-slate-200 dark:bg-slate-700 text-slate-500 dark:text-slate-400'
                )}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function RequestTable({
  title,
  requests,
  onRequestClick,
  statusColor = 'red'
}: {
  title: string;
  requests: Array<{ url: string; time: number; status?: number; explanation?: string }>;
  onRequestClick: (entry: HAREntry) => void;
  statusColor?: 'red' | 'amber';
}) {
  const { filteredEntries } = useHARStore();

  const matchUrl = (url: string): HAREntry | null => {
    return filteredEntries.find(e =>
      e.request.url.toLowerCase() === url.toLowerCase() ||
      e.request.url.toLowerCase().includes(url.toLowerCase().split('?')[0])
    ) || null;
  };

  const colorClasses = {
    red: {
      bg: 'bg-red-100 dark:bg-red-500/20',
      text: 'text-red-700 dark:text-red-400',
      border: 'border-red-200 dark:border-red-500/30'
    },
    amber: {
      bg: 'bg-amber-100 dark:bg-amber-500/20',
      text: 'text-amber-700 dark:text-amber-400',
      border: 'border-amber-200 dark:border-amber-500/30'
    }
  };

  const color = colorClasses[statusColor];

  if (requests.length === 0) return null;

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
      <div className={cn('px-4 py-2 text-sm font-semibold border-b border-slate-200 dark:border-slate-700', color.bg, color.text)}>
        {title}
      </div>
      <div className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900/50">
        {requests.map((req, index) => {
          const entry = matchUrl(req.url);
          const displayUrl = getFileName(req.url);

          return (
            <button
              key={index}
              onClick={() => entry && onRequestClick(entry)}
              disabled={!entry}
              className={cn(
                'w-full text-left p-3 hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-colors',
                !entry && 'opacity-50 cursor-not-allowed'
              )}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    {req.status && (
                      <span className={cn(
                        'text-xs font-bold px-1.5 py-0.5 rounded',
                        color.bg, color.text
                      )}>
                        {req.status}
                      </span>
                    )}
                    <span className="text-sm font-medium text-slate-900 dark:text-slate-100 truncate">
                      {displayUrl}
                    </span>
                  </div>
                  {req.explanation && (
                    <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                      {req.explanation}
                    </p>
                  )}
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className={cn(
                    'text-sm font-semibold',
                    req.time > 3000 ? 'text-red-500' :
                    req.time > 1000 ? 'text-amber-500' :
                    'text-slate-500'
                  )}>
                    {formatTime(req.time)}
                  </span>
                  {entry ? (
                    <ArrowUpRight className="w-4 h-4 text-slate-400" />
                  ) : (
                    <span className="text-[10px] text-slate-400">not found</span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AIBasicSummary() {
  const { aiInsights, aiInsightsLoading, loadAIInsights, selectEntry, setSidebarOpen } = useHARStore();

  useEffect(() => {
    if (!aiInsights && !aiInsightsLoading) {
      loadAIInsights();
    }
  }, [aiInsights, aiInsightsLoading, loadAIInsights]);

  const handleRequestClick = (entry: HAREntry) => {
    selectEntry(entry);
    setSidebarOpen(true);
  };

  if (aiInsightsLoading) {
    return (
      <div className="flex items-center justify-center p-8 bg-violet-50 dark:bg-violet-500/10 rounded-xl border border-violet-200 dark:border-violet-500/20">
        <Loader2 className="w-5 h-5 animate-spin text-violet-500 mr-3" />
        <span className="text-sm text-violet-600 dark:text-violet-400">AI is analyzing your HAR file...</span>
      </div>
    );
  }

  if (!aiInsights) {
    return (
      <div className="text-center p-6">
        <button
          onClick={() => loadAIInsights()}
          className="btn-primary"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          Analyze with AI
        </button>
      </div>
    );
  }

  const insight = aiInsights[0] as unknown as AIBasicInsight;

  if (!insight || !insight.overallStatus) {
    return (
      <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl border border-emerald-200 dark:border-emerald-500/20 text-center">
        <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
        <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">No issues found</p>
        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">Your HAR file looks healthy!</p>
      </div>
    );
  }

  const statusConfig = {
    healthy: { icon: CheckCircle, color: 'emerald', bg: 'bg-emerald-50 dark:bg-emerald-500/10', border: 'border-emerald-200 dark:border-emerald-500/20' },
    degraded: { icon: AlertTriangle, color: 'amber', bg: 'bg-amber-50 dark:bg-amber-500/10', border: 'border-amber-200 dark:border-amber-500/20' },
    critical: { icon: XCircle, color: 'red', bg: 'bg-red-50 dark:bg-red-500/10', border: 'border-red-200 dark:border-red-500/20' }
  };

  const config = statusConfig[insight.overallStatus];
  const Icon = config.icon;

  return (
    <div className="space-y-4">
      <div className={cn('p-4 rounded-xl border text-center', config.bg, config.border)}>
        <Icon className={cn('w-8 h-8 mx-auto mb-2', `text-${config.color}-500`)} />
        <p className={cn('text-sm font-semibold', `text-${config.color}-700 dark:text-${config.color}-300`)}>
          {insight.summary}
        </p>
      </div>

      {insight.errorPatterns && insight.errorPatterns.length > 0 && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className="px-4 py-2 text-sm font-semibold bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
            Error Patterns
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900/50 p-3 space-y-2">
            {insight.errorPatterns.map((pattern, index) => (
              <ErrorPatternGroup key={index} pattern={pattern} />
            ))}
          </div>
        </div>
      )}

      {insight.failedRequests && insight.failedRequests.length > 0 && (
        <RequestTable
          title="Failed Requests (Click for Details)"
          requests={insight.failedRequests}
          onRequestClick={handleRequestClick}
          statusColor="red"
        />
      )}

      {insight.slowRequests && insight.slowRequests.length > 0 && (
        <RequestTable
          title="Slow Requests (Click for Details)"
          requests={insight.slowRequests}
          onRequestClick={handleRequestClick}
          statusColor="amber"
        />
      )}

      {insight.recommendations && insight.recommendations.length > 0 && (
        <div className="border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
          <div className="px-4 py-2 text-sm font-semibold bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300">
            Recommended Actions
          </div>
          <div className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900/50">
            {insight.recommendations.map((rec, index) => (
              <div key={index} className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400">
                {rec}
              </div>
            ))}
          </div>
        </div>
      )}

      {insight.overallStatus === 'healthy' && (
        <div className="p-4 bg-emerald-50 dark:bg-emerald-500/10 rounded-xl border border-emerald-200 dark:border-emerald-500/20 text-center">
          <CheckCircle className="w-8 h-8 text-emerald-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-emerald-700 dark:text-emerald-300">Everything looks good!</p>
          <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1">No errors or slow requests detected.</p>
        </div>
      )}
    </div>
  );
}

export function BasicDashboard() {
  const { filteredEntries, stats, aiInsights, loadAIInsights, filters } = useHARStore();

  const userContext = useMemo((): UserContext | null => {
    if (!filteredEntries.length) return null;

    for (const entry of filteredEntries.slice(0, 10)) {
      const uaHeader = entry.request.headers.find(h => h.name.toLowerCase() === 'user-agent');
      if (uaHeader?.value) {
        return parseUserAgent(uaHeader.value);
      }
    }

    return null;
  }, [filteredEntries]);

  const errorCounts = useMemo(() => {
    if (!stats) return { errors: 0, auth: 0, notfound: 0, server: 0 };
    return {
      errors: stats.errors,
      auth: filteredEntries.filter(e => e.response.status === 401 || e.response.status === 403).length,
      notfound: filteredEntries.filter(e => e.response.status === 404).length,
      server: filteredEntries.filter(e => e.response.status >= 500).length,
    };
  }, [stats, filteredEntries]);

  const isFiltered = Boolean(
    filters.search ||
    filters.methods.length > 0 ||
    filters.statusCodes.length > 0 ||
    filters.contentTypes.length > 0 ||
    filters.domains.length > 0 ||
    filters.minSize !== undefined ||
    filters.maxSize !== undefined ||
    filters.minTime !== undefined ||
    filters.maxTime !== undefined
  );

  if (!stats) return null;

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
      <div className="text-center space-y-2">
        <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
          HAR Analysis Summary
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400">
          Quick overview for support engineers
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatPill icon={Activity} label="Requests" value={stats.totalRequests.toString()} />
        <StatPill icon={Clock} label="Total Time" value={formatTime(stats.totalTime)} />
        <StatPill
          icon={HardDrive}
          label="Total Size"
          value={formatBytes(stats.totalSize)}
          status={stats.totalSize > 5 * 1024 * 1024 ? 'warning' : undefined}
        />
        <StatPill
          icon={AlertCircle}
          label="Errors"
          value={stats.errors.toString()}
          status={stats.errors > 0 ? 'bad' : undefined}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatPill
          icon={AlertOctagon}
          label="Auth Issues"
          value={errorCounts.auth.toString()}
          status={errorCounts.auth > 0 ? 'bad' : undefined}
        />
        <StatPill
          icon={EyeOff}
          label="Not Found"
          value={errorCounts.notfound.toString()}
          status={errorCounts.notfound > 0 ? 'warning' : undefined}
        />
        <StatPill
          icon={Server}
          label="Server Errors"
          value={errorCounts.server.toString()}
          status={errorCounts.server > 0 ? 'bad' : undefined}
        />
        <StatPill
          icon={Zap}
          label="Slow (>1s)"
          value={filteredEntries.filter(e => e.time > 1000).length.toString()}
          status={filteredEntries.filter(e => e.time > 1000).length > 5 ? 'warning' : undefined}
        />
      </div>

      {userContext && <UserContextCard context={userContext} />}

      <FilterPresets />

      {isFiltered && (
        <div className="p-3 bg-violet-50 dark:bg-violet-500/10 rounded-lg border border-violet-200 dark:border-violet-500/20 text-center">
          <p className="text-xs text-violet-600 dark:text-violet-400">
            Filter active • {filteredEntries.length} of {stats.totalRequests} requests shown
          </p>
        </div>
      )}

      <div className="card p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">
            AI Analysis
          </h2>
          <button
            onClick={() => loadAIInsights()}
            disabled={aiInsights !== null}
            className="text-xs text-violet-600 dark:text-violet-400 hover:underline flex items-center gap-1"
          >
            <Sparkles className="w-3 h-3" />
            {aiInsights ? 'Analyzed' : 'Analyze'}
          </button>
        </div>
        <AIBasicSummary />
      </div>

      {filteredEntries.length > 0 && (
        <div className="card p-6">
          <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-4">
            Top Slowest Requests
          </h2>
          <div className="space-y-2">
            {[...filteredEntries]
              .sort((a, b) => b.time - a.time)
              .slice(0, 5)
              .map((entry, index) => (
                <button
                  key={entry._id || index}
                  onClick={() => {
                    useHARStore.getState().selectEntry(entry);
                    useHARStore.getState().setSidebarOpen(true);
                  }}
                  className="w-full text-left p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors flex items-center justify-between gap-3"
                >
                  <span className="text-sm text-slate-700 dark:text-slate-300 truncate flex-1">
                    {getFileName(entry.request.url)}
                  </span>
                  <span className={cn(
                    'text-sm font-medium flex-shrink-0',
                    entry.time > 3000 && 'text-red-500',
                    entry.time > 1000 && entry.time <= 3000 && 'text-amber-500',
                    entry.time <= 1000 && 'text-emerald-500'
                  )}>
                    {formatTime(entry.time)}
                  </span>
                </button>
              ))}
          </div>
        </div>
      )}
    </div>
  );
}
